import {useSuspenseQuery} from '@tanstack/react-query'
import {HiChevronRight} from 'react-icons/hi2'
import {Link} from '@tanstack/react-router'
import React, {memo, Suspense} from 'react'
import {useTranslations} from 'use-intl'
import {HiX} from 'react-icons/hi'
import clsx from 'clsx'
import {productFilterQueryOptions} from '~/client/fn/productFilter'
import {collectionsQueryOptions} from '~/client/fn/collections'
import SubMenu from '~/components/navigation/SubMenu'
import useLocaleParams from '~/hooks/useLocaleParams'

type MobileMenuProps = {
    isVisible: boolean
    onClose: () => void
}

const MobileMenu = ({isVisible, onClose}: MobileMenuProps) => {
    const params = useLocaleParams()
    const t = useTranslations('navigation')

    return (
        <aside
            className={clsx(
                'top-0 z-50 fixed h-full sm:px-6 w-screen bg-black/40 transition-transform motion-reduce:transition-none duration-500 ease-in-out backdrop-blur-xl transform-gpu flex flex-col',
                isVisible ? 'translate-x-0' : '-translate-x-full'
            )}
            // @ts-ignore
            inert={!isVisible ? 'true' : undefined}
        >
            <div className="flex justify-end pb-4">
                <button type="button" onClick={onClose} aria-label={t('closeMenu')} className="p-4">
                    <HiX aria-hidden="true" className="w-6 h-6" />
                </button>
            </div>
            <ul
                role="list"
                className="flex flex-col gap-6 text-xl overflow-y-auto  overscroll-contain pb-8 mx-4"
            >
                <li>
                    <Link
                        to="/$lang/heart-or-pass"
                        params={params}
                        onClick={onClose}
                        className="px-4 py-2 inline-flex justify-between items-center min-w-full sm:min-w-[75%] font-medium bg-gray-300/10 rounded"
                    >
                        <span>{t('menu.perfectFit')}</span>
                        <HiChevronRight aria-hidden="true" className="size-6" />
                    </Link>
                </li>
                <li>
                    <Link
                        to="/$lang/collections"
                        params={params}
                        onClick={onClose}
                        className="px-4 py-2 inline-flex justify-between items-center min-w-full sm:min-w-[75%] font-medium bg-gray-300/10 border-b border-white/30 rounded-t"
                    >
                        <span>{t('menu.designCollections')}</span>
                        <HiChevronRight aria-hidden="true" className="size-6" />
                    </Link>
                    <Suspense>
                        <DesignCollectionList closeMenu={onClose} />
                    </Suspense>
                </li>
                <li>
                    <Link
                        to="/$lang/products"
                        params={params}
                        onClick={onClose}
                        className="px-4 py-2 inline-flex justify-between items-center min-w-full sm:min-w-[75%] font-medium bg-gray-300/10 border-b border-white/30 rounded-t"
                    >
                        <span>{t('menu.products')}</span>
                        <HiChevronRight aria-hidden="true" className="size-6" />
                    </Link>
                    <Suspense>
                        <ProductDepartmentList closeMenu={onClose} />
                    </Suspense>
                </li>
            </ul>
        </aside>
    )
}

type SubListProps = {
    closeMenu: () => void
}

const DesignCollectionList = ({closeMenu}: SubListProps) => {
    const params = useLocaleParams()
    const {data: collections} = useSuspenseQuery(collectionsQueryOptions(params.locale))

    if (!collections?.length) {
        return null
    }

    return (
        <SubMenu
            className="text-lg [&>*:last-child>*]:rounded-b"
            onClose={closeMenu}
            items={collections.map((collection) => ({
                className: 'px-4 py-2 min-w-full sm:min-w-[75%] bg-gray-300/10',
                id: collection?.id!,
                to: '/$lang/collections/$collectionId',
                params: {...params, collectionId: collection.id},
                label: collection.name,
            }))}
        />
    )
}

const ProductDepartmentList = memo(({closeMenu}: SubListProps) => {
    const params = useLocaleParams()
    const {data: filters} = useSuspenseQuery(productFilterQueryOptions())

    if (!filters?.categories?.length) {
        return null
    }

    return (
        <SubMenu
            className="text-lg [&>*:last-child>*]:rounded-b"
            onClose={closeMenu}
            items={filters.categories.map((department) => ({
                className: 'px-4 py-2 min-w-full sm:min-w-[75%]  bg-gray-300/10',
                id: department?.id!,
                to: '/$lang/products',
                params,
                search: {department: department?.id ?? undefined},
                label: department?.name!,
            }))}
        />
    )
})

export default MobileMenu
